import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, setCsrfToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  // undefined = loading, null = not authenticated, object = authenticated
  const [user, setUser] = useState(undefined);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setCsrfToken(data.csrf_token);
      setUser(data);
      return data;
    } catch {
      // Bootstrap-race guard: never wipe a user that a concurrent login() already set.
      setUser((prev) => (prev ? prev : null));
      return null;
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const login = useCallback(async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setCsrfToken(data.csrf_token);
    setUser(data);
    return data;
  }, []);

  const logout = useCallback(async () => {
    // Best-effort: never let a failed logout request throw / cause an uncaught rejection.
    try {
      await api.post("/auth/logout");
    } catch {
      // ignore network / auth errors; we still clear local state below
    }
    setCsrfToken("");
    setUser(null);
  }, []);

  return (
    <AuthContext.Provider value={{ user, setUser, login, logout, refresh }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
